import {
  ChartConfiguration,
  DimensionDescriptor,
  MeasureDescriptor,
  MetricDescriptor,
  PivotConfiguration,
  ReportConfiguration,
  ReportType,
  TabularConfiguration,
  TileConfiguration,
} from "../../../shared/reporting/api/biClient.types";
import {
  isChartReportType,
  isPivotReportType,
  isTabularReportType,
  isTileReportType,
} from "../../../shared/reporting/utils/reportTypeHelper";
import { addMissingGroupSorts, setGroupingFlag } from "../../components/builder/common/utilities/sortFieldStateHelper";
import { configurationToArguments } from "../../components/builder/utils/configuration/chartConfiguration";
import {
  configurationToConditions,
  configurationToSorts,
} from "../../components/builder/utils/configuration/configurations";
import {
  configurationToColumns,
  configurationToRows,
  configurationToValues,
  configurationToSettings as pivotConfigurationToSettings,
} from "../../components/builder/utils/configuration/pivotConfigurations";
import {
  configurationToGrouping,
  configurationToTabularFields,
  configurationToTabularSettings as tabularConfigurationToSettings,
} from "../../components/builder/utils/configuration/tabularConfiguration";
import {
  configurationToTileFields,
  configurationToTileSettings,
} from "../../components/builder/utils/configuration/tileConfiguration";
import { AppDispatch } from "../store";
import { fieldsStateActions } from "../thunks/fieldStatesThunks";

export const setFieldStatesConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  configuration: ReportConfiguration,
  reportType: ReportType,
  dispatch: AppDispatch
) => {
  if (isTabularReportType(reportType)) {
    setTabularConfiguration(dimensions, measures, metrics, configuration, dispatch);
  } else if (isPivotReportType(reportType)) {
    setPivotReportConfiguration(dimensions, measures, metrics, configuration, dispatch);
  } else if (isChartReportType(reportType)) {
    setChartReportConfiguration(dimensions, measures, metrics, configuration, dispatch);
  } else if (isTileReportType(reportType)) {
    setTileReportConfiguration(dimensions, measures, metrics, configuration, dispatch);
  } else {
    throw new Error("Report type not supported");
  }
};

const setTileReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as TileConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.fields) {
    const result = configurationToTileFields(configuration.fields, dimensions, measures, metrics);
    dispatch(fieldsStateActions.setFields(result));
  }
  if (configuration.settings) {
    const result = configurationToTileSettings(configuration.settings);
    dispatch(fieldsStateActions.setSettings({ settings: result }));
  }
};

const setChartReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as ChartConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.arguments) {
    const result = configurationToArguments(configuration.arguments, dimensions);
    dispatch(fieldsStateActions.setArguments(result));
  }
  if (configuration.values) {
    const result = configurationToValues(configuration.values, measures, metrics, dimensions);
    dispatch(fieldsStateActions.setMeasures(result));
  }
  if (configuration.sort) {
    const result = configurationToSorts(configuration.sort, dimensions);
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames: [] }));
  }
  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};

const setPivotReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as PivotConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.rows) {
    const result = configurationToRows(configuration.rows, dimensions);
    dispatch(fieldsStateActions.setRows(result));
  }
  if (configuration.columns) {
    const result = configurationToColumns(configuration.columns, dimensions);
    dispatch(fieldsStateActions.setColumns(result));
  }
  if (configuration.values) {
    const result = configurationToValues(configuration.values, measures, metrics, dimensions);
    dispatch(fieldsStateActions.setMeasures(result));
  }
  if (configuration.sort) {
    const result = configurationToSorts(configuration.sort, dimensions);
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames: [] }));
  }
  if (configuration.settings) {
    const result = pivotConfigurationToSettings(configuration.settings);
    dispatch(fieldsStateActions.setSettings({ settings: result }));
  }
};

const setTabularConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as TabularConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }

  if (configuration.fields) {
    const result = configurationToTabularFields(configuration.fields, dimensions, measures, metrics);
    dispatch(fieldsStateActions.setFields(result));
  }

  if (configuration.sort) {
    const sorts = addMissingGroupSorts(
      configuration.fields,
      configuration.grouping,
      setGroupingFlag(configuration.fields ?? [], configuration.grouping ?? [], configuration.sort ?? [])
    );
    const result = configurationToSorts(sorts, dimensions);
    const groupMetaNames = configuration.grouping.map(
      (g) => configuration.fields.find((f) => f.guid === g.id)?.name ?? ""
    );
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames }));
  }

  if (configuration.grouping) {
    const result = configurationToGrouping(configuration.grouping);
    dispatch(fieldsStateActions.setGrouping(result));
  }

  if (configuration.settings) {
    const result = tabularConfigurationToSettings(configuration.settings);
    dispatch(fieldsStateActions.setSettings({ settings: result }));
  }
};
